import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBuildingOffice2,
  HiOutlinePencil,
  HiOutlineTrash
} from 'react-icons/hi2';
import { authService, areaService, userService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from 'react-toastify';
import PageLoading from '../components/common/PageLoading';
import SharedModal from '../components/common/SharedModal';
import './Areas.css';

const Areas = () => {
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to show page immediately
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [formData, setFormData] = useState({
    areaName: '',
    status: 'Active',
    areaHeadId: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('Area.View');
  const canCreate = hasPermission('Area.Create');
  const canEdit = hasPermission('Area.Edit');
  const canDelete = hasPermission('Area.Delete');

  useEffect(() => {
    if (tenantId && canView) {
      // Load data in background without blocking UI
      loadData(false);
    } else if (!canView) {
      setError('You do not have permission to view areas');
    } else {
      setError('Please login to view areas');
    }
  }, [tenantId, canView]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const [areasData, usersData] = await Promise.all([
        areaService.getAllAreas(),
        userService.getAllUsers()
      ]);
      setAreas(areasData || []);
      setUsers(usersData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
      setAreas([]);
      setUsers([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAreas = areas.filter((area) =>
    area.areaName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    if (!canCreate) {
      setError('You do not have permission to create areas');
      return;
    }
    setEditingArea(null);
    setFormData({
      areaName: '',
      status: 'Active',
      areaHeadId: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (area) => {
    if (!canEdit) {
      setError('You do not have permission to edit areas');
      return;
    }
    setEditingArea(area);
    setFormData({
      areaName: area.areaName || '',
      status: area.status || 'Active',
      areaHeadId: area.areaHeadId?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const toastId = toast.loading('Deleting area...');
    try {
      setError('');
      await areaService.deleteArea(id);
      await loadData(false); // Don't show loading on refresh after delete
      setDeleteConfirm(null);
      toast.update(toastId, { render: 'Area deleted successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const msg = 'Failed to delete area. Please try again.';
      setError(msg);
      toast.update(toastId, { render: msg, type: 'error', isLoading: false, autoClose: 3000, closeButton: true });
      console.error('Error deleting area:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.areaName.trim()) {
      errors.areaName = 'Area name is required';
    } else if (formData.areaName.length > 100) {
      errors.areaName = 'Area name must be less than 100 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (editingArea && !canEdit) {
      setError('You do not have permission to edit areas');
      return;
    }
    if (!editingArea && !canCreate) {
      setError('You do not have permission to create areas');
      return;
    }

    if (!validateForm()) {
      return;
    }

    const toastId = toast.loading(editingArea ? 'Updating area...' : 'Creating area...');
    try {
      setSubmitting(true);
      setError('');

      const areaData = {
        areaName: formData.areaName.trim(),
        status: formData.status,
        areaHeadId: formData.areaHeadId ? parseInt(formData.areaHeadId) : null,
      };

      if (editingArea) {
        areaData.areaId = editingArea.areaId;
        await areaService.updateArea(editingArea.areaId, areaData);
      } else {
        await areaService.createArea(areaData);
      }

      await loadData(false); // Don't show loading on refresh after save
      setShowModal(false);
      setFormData({ areaName: '', status: 'Active', areaHeadId: '' });
      setEditingArea(null);
      toast.update(toastId, { render: editingArea ? 'Area updated successfully!' : 'Area created successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.message ||
        'Failed to save area. Please try again.';
      setError(errorMessage);
      toast.update(toastId, { render: errorMessage, type: 'error', isLoading: false, autoClose: 3000, closeButton: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingArea(null);
    setFormData({ areaName: '', status: 'Active', areaHeadId: '' });
    setFormErrors({});
  };

  const getAreaHeadName = (userId) => {
    if (!userId) return '-';
    // Use loose equality to handle string/number type mismatch
    const user = users.find(u => u.userId == userId);
    return user ? user.fullName : '-';
  };

  return (
    <div className="pagecontainer areas-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Areas</h1>
          <p>Manage your organization areas</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search areas..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>

          {canCreate && (
            <button className="btn-primary btn" onClick={handleAdd}>
              <HiOutlinePlus />
              Add Area
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        {loading && areas.length === 0 ? (
          <PageLoading message="Loading areas..." />
        ) : filteredAreas?.length === 0 ? (
          <div className="empty-state">
            <HiOutlineBuildingOffice2 className="empty-icon" />
            <h3>No areas found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first area'}
            </p>
            {!searchTerm && canCreate && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Area
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Area Name</th>
                <th>Area Head</th>
                <th>Status</th>
                <th>Created At</th>
                <th className='text-center' style={{ width: "120px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAreas.map((area) => (
                <tr key={area.areaId}>
                  <td>{area.areaId}</td>
                  <td className="name-cell">{area.areaName}</td>
                  <td>{getAreaHeadName(area.areaHeadId)}</td>
                  <td>
                    <span className={`status-badge ${area.status?.toLowerCase()}`}>
                      {area.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {area.createdAt
                      ? new Date(area.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(area)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(area)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingArea ? 'Edit Area' : 'Add New Area'}
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="area-form"
              className="btn-primary btn"
              disabled={submitting}
            >
              {submitting
                ? editingArea ? 'Updating...' : 'Creating...'
                : editingArea ? 'Update' : 'Create'}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <form id="area-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="areaName" className="form-label">
                Area Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="areaName"
                value={formData.areaName}
                onChange={(e) =>
                  setFormData({ ...formData, areaName: e.target.value })
                }
                className={formErrors.areaName ? 'error' : ''}
                placeholder="Enter area name"
                maxLength={100}
              />
              {formErrors.areaName && (
                <span className="field-error">{formErrors.areaName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="areaHeadId" className="form-label">Area Head</label>
              <select
                id="areaHeadId"
                value={formData.areaHeadId}
                onChange={(e) =>
                  setFormData({ ...formData, areaHeadId: e.target.value })
                }
              >
                <option value="">Select Area Head (Optional)</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId.toString()}>
                    {user.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', width: '100%'}}>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={() => handleDelete(deleteConfirm.areaId)}
            >
              Delete
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.areaName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Areas;
